import React from 'react';
import cx from 'classnames';
import { find } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { InfoAlert } from '@magoosh/lib/alerts';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { formatPrice } from 'utilities/money';

import { PricingComponent, ReplacedAgreement, SubscriptionPlan } from '@payments/types';
import * as utils from '@payments/utils';

import style from './style.module.scss';

interface Props {
  isCalculatingPricing: boolean;
  isInstallments: boolean;
  listTotal: number;
  pricingComponents: PricingComponent[];
  // What the plan costs every payment after the first, and the sales tax inside that amount.
  // Undefined on a plan bought outright, which never has a second payment.
  renewalTax?: number;
  renewalTotal?: number;
  replacedAgreement?: ReplacedAgreement;
  subscriptionPlan: SubscriptionPlan;
}

const Confirmation: React.FC<Props> = (props) => {
  const lsacComponent = find(props.pricingComponents, (component) => component.type === 'lsac');
  const savingsComponent = find(props.pricingComponents, (component) => component.type === 'savings');
  const taxComponent = find(props.pricingComponents, (component) => component.type === 'tax');

  const totalPrice = props.pricingComponents.reduce((sum, component) => (sum + component.priceInCents), 0);

  const showsRenewalPrice = props.renewalTotal != null && props.renewalTotal !== totalPrice;

  return (
    <div>
      {
        // Buying a plan while on one that can be changed replaces it rather than adding to it
        props.replacedAgreement && (
          <InfoAlert className="u-margin-B-s">
            <small>
              Your current plan, <strong>{props.replacedAgreement.displayName}</strong>, will be
              cancelled when you complete this purchase, and you&apos;ll be refunded for the time you
              haven&apos;t used.
            </small>
          </InfoAlert>
        )
      }

      <div className="u-margin-B-s">
        <h4 className="u-margin-V-n inline-form-right">
          <strong>{props.subscriptionPlan.name}</strong>
        </h4>
        <p className="inline-form-right-help extra-bottom-margin">
          <small className="text-gray-light">
            {
              props.subscriptionPlan.recurring
                ? `Renews automatically every ${utils.billingPeriod(props.subscriptionPlan)}`
                : `Expires on ${utils.calculateExpirationDate(props.subscriptionPlan)}`
            }
          </small>
        </p>
      </div>

      <div className="d-flex">
        <div style={{ minWidth: 20 }}>
          {
            props.isCalculatingPricing
              ? <FontAwesomeIcon icon="spinner" className="fa-pulse small text-gray-light" />
              : <FontAwesomeIcon icon="angle-double-right" className="text-gray-light" />
          }
        </div>

        <div>
          <span className="h4">
            <strong>{formatPrice(totalPrice)}</strong>
          </span>
          
          {
            props.subscriptionPlan.recurring && (
              <>
                {' '}
                <small className="text-gray-light">
                  {
                    // A discount that only covers the first payment isn't what they pay every
                    // period, so price it as the first period rather than as the ongoing rate
                    showsRenewalPrice
                      ? `first ${utils.billingPeriod(props.subscriptionPlan)}`
                      : `/${utils.billingPeriod(props.subscriptionPlan)}`
                  }
                </small>
              </>
            )
          }

          {
            savingsComponent && savingsComponent.priceInCents !== 0 && (
              <>
                {' '}
                <small className="text-gray-light">
                  <s>{formatPrice(props.listTotal)}</s> with {savingsComponent.name}
                </small>
              </>
            )
          }

          {
            showsRenewalPrice && !!taxComponent?.priceInCents && (
              <>
                {' '}
                <small className="text-gray-light">
                  (including {formatPrice(taxComponent.priceInCents)} sales tax)
                </small>
              </>
            )
          }

          <div style={{ minHeight: lsacComponent ? 48 : 24 }}>
            {
              !!lsacComponent?.priceInCents && (
                <div>
                  <small>
                    including {formatPrice(lsacComponent.priceInCents)} one-time LawHub Advantage fee
                    (nonrefundable
                    {' '}
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          The LawHub Advantage fee goes directly to LSAC, is nonrefundable, and is not included in our 7-day money back guarantee.
                        </Tooltip>
                      }
                    >
                      <span>
                        <FontAwesomeIcon icon="question-circle" />
                      </span>
                    </OverlayTrigger>
                    )
                  </small>
                </div>
              )
            }
            {
              !props.isInstallments && !showsRenewalPrice && !!taxComponent?.priceInCents && (
                <div>
                  <small>including {formatPrice(taxComponent.priceInCents)} sales tax</small>
                </div>
              )
            }
            {
              // The price above covers only the first period when a discount falls off after it,
              // so spell out what they will pay from the next payment on
              props.renewalTotal != null && props.renewalTotal !== totalPrice && (
                <div>
                  <small>
                    Then <strong>{formatPrice(props.renewalTotal)}</strong>
                  </small>
                  {' '}
                  <small className="text-gray-light">
                    /{utils.billingPeriod(props.subscriptionPlan)}
                    {' '}
                    starting {utils.calculateExpirationDate(props.subscriptionPlan)}
                    {
                      !!props.renewalTax && (
                        ` (including ${formatPrice(props.renewalTax)} sales tax)`
                      )
                    }
                  </small>
                </div>
              )
            }
            {
              props.isInstallments && (
                <div>
                  <strong>Each Installment: {formatPrice(utils.calculateInstallmentsPrice(totalPrice))}</strong>
                  {
                    !!taxComponent?.priceInCents && (
                      <small> (including {formatPrice(utils.calculateInstallmentsPrice(taxComponent.priceInCents))} sales tax)</small>
                    )
                  }
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
